import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const emergentAuth = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/orders";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      nav(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md p-8 bg-white rounded-3xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="font-display font-extrabold text-xl">SupplySprout</span>
        </div>
        <h1 className="font-display text-2xl font-extrabold">Welcome back</h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">Sign in to track your orders & auto-fulfillment.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="login-submit-btn">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
          <div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" />
        </div>
        <Button onClick={emergentAuth} variant="outline" className="w-full rounded-full" data-testid="login-google-btn">
          Continue with Google
        </Button>
        <div className="mt-6 text-sm text-slate-600 text-center">
          New here? <Link to="/register" className="font-semibold text-indigo-600 hover:underline">Create an account</Link>
        </div>
      </div>
    </div>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "parent" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Welcome to SupplySprout!");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md p-8 bg-white rounded-3xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="font-display font-extrabold text-xl">SupplySprout</span>
        </div>
        <h1 className="font-display text-2xl font-extrabold">Create your account</h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">Parents & teachers — join in seconds.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="reg-email">Email</Label>
            <Input id="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="reg-password">Password (min 6 characters)</Label>
            <Input id="reg-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" />
          </div>
          <div>
            <Label>I am a…</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="teacher">Teacher / Educator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="register-submit-btn">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-500">
          <div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" />
        </div>
        <Button onClick={emergentAuth} variant="outline" className="w-full rounded-full" data-testid="register-google-btn">
          Continue with Google
        </Button>
        <div className="mt-6 text-sm text-slate-600 text-center">
          Have an account? <Link to="/login" className="font-semibold text-indigo-600 hover:underline">Log in</Link>
        </div>
      </div>
    </div>
  );
};
